import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import ServicesSection from "@/components/ServicesSection";
import WhyZenithSection from "@/components/WhyZenithSection";
import TestimonialsSection from "@/components/TestimonialsSection";
import FAQSection from "@/components/FAQSection";
import SolarCTASection from "@/components/SolarCTASection";
import TrustedPartnersSection from "@/components/TrustedPartnersSection";

import FooterSection from "@/components/FooterSection";
import ServiceAreasSection from "@/components/ServiceAreasSection";

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": ["RoofingContractor", "GeneralContractor", "SolarEnergyCompany"],
  "@id": "https://zenithsolutionsnm.com/#business",
  name: "Zenith Solutions",
  legalName: "Zenith Solutions LLC",
  description:
    "Albuquerque's top-rated roofing contractor, general construction company, solar panel installer, and mold remediation specialist. Licensed, insured, A+ BBB rated. Serving all of Central New Mexico.",
  url: "https://zenithsolutionsnm.com",
  telephone: "+15056048536",
  email: "info@zenithsolutionsnm.com",
  image: "https://zenithsolutionsnm.com/og-image.jpg",
  logo: "https://zenithsolutionsnm.com/favicon.webp",
  foundingDate: "2018",
  numberOfEmployees: { "@type": "QuantitativeValue", minValue: 10, maxValue: 50 },
  address: {
    "@type": "PostalAddress",
    streetAddress: "Albuquerque",
    addressLocality: "Albuquerque",
    addressRegion: "NM",
    postalCode: "87101",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 35.0844,
    longitude: -106.6504,
  },
  areaServed: [
    { "@type": "City", name: "Albuquerque", "@id": "https://en.wikipedia.org/wiki/Albuquerque,_New_Mexico" },
    { "@type": "City", name: "Rio Rancho" },
    { "@type": "City", name: "Corrales" },
    { "@type": "City", name: "Los Lunas" },
    { "@type": "City", name: "Bernalillo" },
    { "@type": "City", name: "Santa Fe" },
    { "@type": "City", name: "Belen" },
    { "@type": "City", name: "Edgewood" },
    { "@type": "City", name: "Moriarty" },
    { "@type": "City", name: "Placitas" },
    { "@type": "City", name: "Tijeras" },
    { "@type": "City", name: "Cedar Crest" },
    { "@type": "City", name: "Los Ranchos de Albuquerque" },
    { "@type": "City", name: "Bosque Farms" },
    { "@type": "City", name: "Peralta" },
    { "@type": "City", name: "Sandia Park" },
    { "@type": "State", name: "New Mexico" },
    {
      "@type": "GeoCircle",
      geoMidpoint: { "@type": "GeoCoordinates", latitude: 35.0844, longitude: -106.6504 },
      geoRadius: "80467",
    },
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    reviewCount: "150",
    bestRating: "5",
    worstRating: "1",
  },
  review: [
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Maria Gutierrez" },
      datePublished: "2025-11-15",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      reviewBody:
        "I couldn't be happier with the new roof installed by Zenith. The team was professional, efficient, and meticulous. Our home feels brand new!",
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "David Chen" },
      datePublished: "2025-09-22",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      reviewBody: "Zenith made renovating my home seamless! Their attention to detail exceeded expectations.",
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Sarah Thompson" },
      datePublished: "2025-08-10",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      reviewBody:
        "Our energy bills have significantly decreased. The solar installation was quick and the team explained everything clearly.",
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "James Rivera" },
      datePublished: "2025-12-01",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      reviewBody:
        "Outstanding craftsmanship and customer service. Zenith replaced our aging tile roof and it looks incredible. They even helped us navigate our insurance claim.",
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Angela Montoya" },
      datePublished: "2026-01-18",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
      reviewBody:
        "From the free consultation to the final walkthrough, Zenith was transparent about everything. No surprises, just quality work delivered on time and on budget.",
    },
  ],
  priceRange: "$$",
  paymentAccepted: "Cash, Credit Card, Financing",
  currenciesAccepted: "USD",
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "07:00",
      closes: "18:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "Saturday",
      opens: "08:00",
      closes: "14:00",
    },
  ],
  sameAs: [],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Roofing, Solar & Construction Services in Albuquerque NM",
    itemListElement: [
      {
        "@type": "OfferCatalog",
        name: "Roofing Services",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Roof Replacement Albuquerque",
              description:
                "Complete residential and commercial roof replacement in Albuquerque NM with asphalt shingles, metal, tile, and slate. Owens Corning preferred contractor.",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Roof Repair Albuquerque",
              description:
                "Storm damage repair, leak fixes, hail damage repair, and emergency roofing services in Albuquerque and Rio Rancho NM.",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "New Roof Installation",
              description:
                "New construction roof installations for residential and commercial buildings throughout New Mexico.",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Roof Inspection",
              description:
                "Free roof inspections and maintenance assessments for Albuquerque homeowners. Storm damage evaluations.",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Insurance Claim Roofing",
              description: "Expert insurance claim assistance for hail and storm damaged roofs in New Mexico.",
            },
          },
        ],
      },
      {
        "@type": "OfferCatalog",
        name: "Solar Services",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Solar Panel Installation Albuquerque",
              description:
                "Residential solar panel installation in Albuquerque NM with NM tax credits, federal ITC guidance, and net metering setup.",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Solar Energy Consultation",
              description:
                "Free solar savings analysis for New Mexico homeowners. Custom system design and financing options.",
            },
          },
        ],
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "General Construction & Remodeling Albuquerque",
          description:
            "Home additions, kitchen and bathroom remodeling, stucco repair, windows, doors, and painting in Albuquerque NM.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Mold Remediation Albuquerque",
          description:
            "Professional mold removal and water damage restoration in Albuquerque. Certified remediation with insurance claim support.",
        },
      },
    ],
  },
  knowsAbout: [
    "Roofing Contractor Albuquerque",
    "Roofer Near Me",
    "Roof Replacement New Mexico",
    "Solar Panel Installation NM",
    "General Contractor Albuquerque",
    "Mold Remediation NM",
    "Owens Corning Preferred Contractor",
    "Storm Damage Roof Repair",
    "Hail Damage Roofing",
    "Roof Inspection Albuquerque",
    "Best Roofer Albuquerque",
    "Roofing Companies Albuquerque NM",
    "Solar Energy New Mexico",
    "Home Remodeling Albuquerque",
    "Stucco Repair NM",
  ],
  slogan: "We educate you first, deliver excellence second.",
  award: ["Owens Corning Preferred Contractor", "A+ BBB Rating"],
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is the best roofing company in Albuquerque NM?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Zenith Solutions is rated Albuquerque's #1 roofing contractor with 150+ five-star reviews, an A+ BBB rating, and Owens Corning Preferred Contractor status. We offer free inspections, transparent pricing, and industry-leading warranties on all roof installations and repairs across the metro area.",
      },
    },
    {
      "@type": "Question",
      name: "How much does a new roof cost in Albuquerque New Mexico?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A new roof in Albuquerque typically costs between $8,000 and $25,000 depending on size, material (asphalt shingles, metal, tile, or slate), and complexity. Zenith Solutions provides free, no-obligation estimates with transparent pricing and flexible financing options including zero-down plans.",
      },
    },
    {
      "@type": "Question",
      name: "What types of roofing materials do you offer in Albuquerque?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We offer asphalt shingles (including Owens Corning Duration and TruDefinition), standing seam metal roofing, clay and concrete tile, and natural slate. Every material is premium-grade, backed by manufacturer warranties, and ideal for New Mexico's desert climate with extreme UV, hail, and monsoon weather.",
      },
    },
    {
      "@type": "Question",
      name: "How long does a roof replacement take in Albuquerque NM?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Most residential roof replacements in Albuquerque take 1–3 days depending on size and complexity. Zenith Solutions provides a detailed timeline upfront and keeps you informed at every stage. We work efficiently to minimize disruption to your daily life.",
      },
    },
    {
      "@type": "Question",
      name: "Do you help with roof insurance claims in New Mexico?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes! Zenith Solutions provides expert insurance claim assistance for hail damage, wind damage, and storm damage roof repairs in Albuquerque and surrounding areas. We document all damage, work directly with your insurance adjuster, and ensure you receive fair compensation.",
      },
    },
    {
      "@type": "Question",
      name: "What are the benefits of installing solar panels in New Mexico?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "New Mexico has 300+ days of sunshine making it ideal for solar. Solar panels reduce electricity bills by 40–70% monthly. NM offers the Solar Market Development Tax Credit (10% of cost up to $6,000). Solar also increases property value by 3-4% and provides energy independence.",
      },
    },
    {
      "@type": "Question",
      name: "How much do solar panels cost in Albuquerque NM?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A typical residential solar system in Albuquerque costs $15,000–$30,000 before incentives. After the NM state tax credit (10%, up to $6,000), net cost drops to $12,000–$24,000. Most homeowners see full ROI in 7–10 years. Zenith Solutions offers free solar savings analyses and flexible financing.",
      },
    },
    {
      "@type": "Question",
      name: "How do I maintain my roof in New Mexico's climate?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We recommend annual inspections, especially after monsoon season (July–September) and hail events. Keep gutters clear, trim overhanging branches, check for UV degradation on shingles, and address minor issues promptly. Zenith Solutions offers maintenance plans and free post-installation inspections.",
      },
    },
    {
      "@type": "Question",
      name: "Do you offer financing for roofing and solar projects?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes! Zenith Solutions partners with leading lenders to offer flexible financing for roofing, construction, and solar projects. Many Albuquerque homeowners qualify for zero-down options with competitive rates. We'll walk you through every option during your free consultation.",
      },
    },
    {
      "@type": "Question",
      name: "Are you a licensed and insured roofing contractor in New Mexico?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Absolutely. Zenith Solutions is fully licensed in New Mexico (NM CID licensed), bonded, and carries comprehensive general liability and workers' compensation insurance. We maintain an A+ BBB rating, are an Owens Corning Preferred Contractor, and stand behind every project with industry-leading warranties.",
      },
    },
    {
      "@type": "Question",
      name: "What areas do you serve in New Mexico?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Zenith Solutions serves Albuquerque, Rio Rancho, Corrales, Los Lunas, Bernalillo, Santa Fe, Belen, Edgewood, Moriarty, Placitas, Tijeras, Cedar Crest, Los Ranchos, Bosque Farms, Peralta, Sandia Park, and all of Central New Mexico within a 50-mile radius of Albuquerque.",
      },
    },
  ],
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Zenith Solutions",
  alternateName: "Zenith Solutions NM",
  url: "https://zenithsolutionsnm.com",
  description:
    "Albuquerque's top-rated roofing, construction, solar, and mold remediation company. Free estimates, A+ BBB rated.",
  publisher: { "@id": "https://zenithsolutionsnm.com/#business" },
  potentialAction: {
    "@type": "SearchAction",
    target: "https://zenithsolutionsnm.com/?q={search_term_string}",
    "query-input": "required name=search_term_string",
  },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [{ "@type": "ListItem", position: 1, name: "Home", item: "https://zenithsolutionsnm.com/" }],
};

const serviceSchemas = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Roof Replacement & Installation in Albuquerque NM",
    description:
      "Professional residential and commercial roof replacement and new roof installation in Albuquerque, New Mexico. Asphalt shingles, metal, tile, slate. Owens Corning Preferred Contractor with lifetime warranties.",
    provider: { "@id": "https://zenithsolutionsnm.com/#business" },
    areaServed: {
      "@type": "City",
      name: "Albuquerque",
      "@id": "https://en.wikipedia.org/wiki/Albuquerque,_New_Mexico",
    },
    serviceType: "Roof Replacement",
    category: "Roofing",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      description: "Free roof inspection and estimate",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Solar Panel Installation Albuquerque NM",
    description:
      "Residential solar panel installation in Albuquerque and Central New Mexico. Expert guidance on NM tax credits, federal ITC, net metering, and financing. 300+ sunny days make NM ideal for solar.",
    provider: { "@id": "https://zenithsolutionsnm.com/#business" },
    areaServed: { "@type": "State", name: "New Mexico" },
    serviceType: "Solar Panel Installation",
    category: "Solar Energy",
  },
];

const Index = () => (
  <>
    <Helmet>
      <title>Zenith Solutions | #1 Roofing Company in Albuquerque NM | Solar & Construction</title>
      <meta
        name="description"
        content="Albuquerque's #1 rated roofing company. Expert roof replacement, repair, solar panel installation & general construction. Licensed, insured, A+ BBB, Owens Corning Preferred Contractor. 1000+ projects. Free estimates — (505) 604-8536."
      />
      <meta
        name="keywords"
        content="roofing company Albuquerque, roofer near me, Albuquerque roofer, roof replacement Albuquerque NM, roofing companies Albuquerque, best roofer Albuquerque, solar panels Albuquerque, solar installation NM, general contractor Albuquerque, mold remediation Albuquerque, storm damage roof repair NM, hail damage roofing, Owens Corning preferred contractor New Mexico, roof repair near me, roofing contractor near me"
      />
      <link rel="canonical" href="https://zenithsolutionsnm.com/" />

      {/* Open Graph */}
      <meta property="og:title" content="Zenith Solutions | #1 Roofing Company in Albuquerque NM" />
      <meta
        property="og:description"
        content="Albuquerque's top-rated roofing, solar & construction company. Licensed, insured, A+ BBB. 1000+ projects completed. Free estimates!"
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://zenithsolutionsnm.com/" />
      <meta property="og:image" content="https://zenithsolutionsnm.com/og-image.jpg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Zenith Solutions - Albuquerque NM Roofing and Solar Company" />
      <meta property="og:site_name" content="Zenith Solutions" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Zenith Solutions | #1 Roofing Company in Albuquerque NM" />
      <meta
        name="twitter:description"
        content="Albuquerque's top-rated roofing & solar company. Free estimates — call (505) 604-8536."
      />
      <meta name="twitter:image" content="https://zenithsolutionsnm.com/og-image.jpg" />

      {/* Geo tags for local SEO */}
      <meta name="geo.region" content="US-NM" />
      <meta name="geo.placename" content="Albuquerque, New Mexico" />
      <meta name="geo.position" content="35.0844;-106.6504" />
      <meta name="ICBM" content="35.0844, -106.6504" />

      {/* Additional SEO meta */}
      <meta name="classification" content="Roofing Contractor" />
      <meta name="subject" content="Roofing, Solar Installation, Construction Services in Albuquerque NM" />
      <meta name="coverage" content="Albuquerque, New Mexico, United States" />
      <meta name="distribution" content="local" />

      <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      {serviceSchemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
    <main className="min-h-screen">
      <Navbar />
      <HeroSection />
      <ServicesSection />
      <WhyZenithSection />
      <TrustedPartnersSection />
      <TestimonialsSection />
      <SolarCTASection />
      <ServiceAreasSection />
      <FAQSection />

      <FooterSection />
    </main>
  </>
);

export default Index;
